import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-hot-toast';
import { authService } from '@/services/authService';
import { teamUserService } from '@/services/teamUserService';
import type {
  User,
  LoginCredentials,
  RegisterCredentials,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  UpdateProfileRequest,
  AuthResponse,
} from '@/types/auth';

// Auth state interface
interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const teamLoginUser = createAsyncThunk(
  'auth/teamLogin',
  async ({ teamId, credentials }: { teamId: string; credentials: LoginCredentials }, { rejectWithValue }) => {
    try {
      const response = await teamUserService.teamLogin(teamId, credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Team login failed');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (credentials: RegisterCredentials, { rejectWithValue }) => {
    try {
      const response = await authService.register(credentials);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (request: ForgotPasswordRequest, { rejectWithValue }) => {
    try {
      await authService.forgotPassword(request);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Forgot password failed');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (request: ResetPasswordRequest, { rejectWithValue }) => {
    try {
      await authService.resetPassword(request);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Reset password failed');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (request: ChangePasswordRequest, { rejectWithValue }) => {
    try {
      await authService.changePassword(request);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Change password failed');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (request: UpdateProfileRequest, { rejectWithValue }) => {
    try {
      const response = await authService.updateProfile(request);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Update profile failed');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.refreshToken();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Token refresh failed');
    }
  }
);

export const restoreAuth = createAsyncThunk(
  'auth/restoreAuth',
  async (_, { rejectWithValue }) => {
    try {
      // Try to get current user from server to restore auth state
      const response = await authService.getCurrentUser();
      return { user: response, token: null };
    } catch (error: any) {
      // If server call fails, user is not authenticated
      console.log('Auth restoration failed:', error.message);
      return { user: null, token: null };
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
    teamUserLogout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.isAuthenticated = true;
        // Extract user data from response structure
        const userData = action.payload.data?.data ?? action.payload.data;
        const merchantUser = {
          ...userData,
          role: 'merchant',
          isTeamUser: false,
        };
        state.user = merchantUser;
        state.token = null; // Using cookies, no token in state
        state.error = null;
        
        toast.success('Login successful');
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      })
      
      // Team Login
      .addCase(teamLoginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(teamLoginUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.isAuthenticated = true;
        // Extract user data from response structure
        const userData = action.payload.data?.data ?? action.payload.data;
        const teamUser = {
          ...userData,
          role: 'MERCHANT_TEAM_USER',
          isTeamUser: true,
        };
        state.user = teamUser;
        state.token = null; // Using cookies, no token in state
        state.error = null;
        
        toast.success('Team login successful');
      })
      .addCase(teamLoginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      })
      
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;
        // Registration doesn't automatically log in the user
        toast.success('Registration successful');
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      })
      
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        toast.success('Password reset email sent');
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      })
      
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        toast.success('Password reset successful');
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      })
      
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        toast.success('Password changed successfully');
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      })
      
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        // Update user data from response
        const userData = action.payload.data?.data ?? action.payload.data;
        const merchantUser = {
          ...userData,
          role: 'merchant',
          isTeamUser: false,
          is_merchant: true,
        };
        state.user = merchantUser;
        state.error = null;
        
        toast.success('Profile updated successfully');
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      })
      
      // Refresh Token
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.token = null; // Using cookies, no token in state
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
      })
      
      // Restore Auth
      .addCase(restoreAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(restoreAuth.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (action.payload.user) {
          state.isAuthenticated = true;
          // Ensure user has proper merchant/team user flags for UI rendering
          const userData = action.payload.user;
          const restoredUser = {
            ...userData,
            is_merchant: userData.is_merchant ?? true, // Default to true for merchant users
            isTeamUser: userData.isTeamUser ?? false, // Default to false for regular merchant users
            role: userData.role ?? 'merchant',
          };
          state.user = restoredUser;
          state.token = action.payload.token || null;
        } else {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
        }
        state.error = null;
      })
      .addCase(restoreAuth.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
      });
  },
});

export const { clearError, logout, teamUserLogout } = authSlice.actions;
export default authSlice;
